import { defineNuxtPlugin, useRouter, useRuntimeConfig } from '#app';

type SentryModule = typeof import('@sentry/vue');

/**
 * Sentry грузится отдельным чанком и инициализируется после простоя.
 * Раньше SDK импортировался статически, попадал в стартовый чанк (~490 КБ)
 * и вешал обёртку на жизненный цикл каждого компонента.
 *
 * Плата за ленивость: транзакция первой загрузки в browserTracing не попадёт.
 * Ошибки ловятся всегда — если что-то падает до инициализации, capture()
 * поднимает SDK немедленно.
 */
export default defineNuxtPlugin(nuxtApp => {
  const {
    public: { sentry },
  } = useRuntimeConfig();

  if (!sentry?.dsn) {
    return;
  }

  const router = useRouter();
  let sdkPromise: Promise<SentryModule> | null = null;

  const loadSentry = () => {
    if (!sdkPromise) {
      sdkPromise = import('@sentry/vue').then(Sentry => {
        Sentry.init({
          app: nuxtApp.vueApp,
          dsn: sentry.dsn,
          environment: sentry.environment,
          autoSessionTracking: true,
          release: `ao-stom@${new Date(Date.now()).toDateString()}`,
          integrations: [
            Sentry.browserTracingIntegration({ router, routeLabel: 'path' }),
            Sentry.httpClientIntegration(),
            Sentry.reportingObserverIntegration(),
          ],
          tracesSampleRate: 0.2,
          allowUrls: ['https://ao-stom.ru', 'http://ao-stom-nuxt.servertest.pro/'],
          tracePropagationTargets: ['https://ao-stom.ru', 'http://ao-stom-nuxt.servertest.pro/'],
        });

        return Sentry;
      });
    }

    return sdkPromise;
  };

  const warmUp = () => {
    loadSentry().catch(() => {});
  };

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(warmUp, { timeout: 5000 });
  } else {
    setTimeout(warmUp, 3000);
  }

  const capture = (err: unknown, context?: unknown) => {
    loadSentry()
      .then(Sentry => {
        if (context === undefined) {
          Sentry.captureException(err);
          return;
        }

        Sentry.withScope(scope => {
          scope.setExtra('context', context);
          Sentry.captureException(err);
        });
      })
      .catch(() => {});
  };

  nuxtApp.vueApp.config.errorHandler = (err, context) => {
    capture(err, context);
  };

  nuxtApp.hook('app:error', err => {
    capture(err);
  });
});
